import { map, type Observable } from 'rxjs';
import type { EventAlarmDetection, EventAlarmsResponse, EventAlarmRow, EventAiTag, EventDetail, EventType } from '../models/entities/event-detail.model';
import { resolveProcessingClaimedAt } from '../utils/processing-claimed-at.util';
import type { EventsInProcessResponse } from '../models/entities/events-in-process.model';
import type { EventsParkedResponse, ParkedEventRow, ParkEventResponse } from '../models/entities/events-parked.model';
import type { EventsQueueResponse, QueuedEvent } from '../models/entities/events-queue.model';
import type { SiteLocation, SiteLocationsResponse } from '../models/entities/site-location.model';
import type { CompletePatrolSessionResponse, StartPatrolSessionResponse } from '../models/entities/patrol-session.model';
import type { PatrolListResponse, PatrolRow } from '../models/entities/patrol-list.model';

interface ApiQueueItem {
  eventId: string;
  siteId: number;
  location: string;
  priority: number;
  alarmCount: number;
  createdDateTime: string;
  firstAlarmDetected: string;
  status: string;
  type?: EventType;
  actorName?: string | null;
  actorAt?: string | null;
  /** Site IANA timezone from the populating endpoint (e.g. America/Chicago). */
  timezone?: string | null;
}

interface ApiQueueResponse {
  totalCount: number;
  items: ApiQueueItem[];
}

interface ApiPatrolListItem {
  id: string;
  patrolStarted: string;
  location: string;
  siteId?: number;
  patrolNote?: string | null;
  agent?: string;
  status?: string;
  decisionOutcome?: string | null;
  timezone?: string | null;
}

interface ApiPatrolListResponse {
  totalCount?: number;
  items?: ApiPatrolListItem[];
}

function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

function resolveDisplayTimeZone(timezone?: string | null): string {
  const requested = timezone?.trim();
  if (requested) {
    try {
      new Intl.DateTimeFormat('en-GB', { timeZone: requested }).format(new Date());
      return requested;
    } catch {
      // Invalid IANA — fall through to the agent browser zone.
    }
  }
  return browserTimeZone();
}

const queueTimestampFormatters = new Map<string, Intl.DateTimeFormat>();
const timezoneAbbreviationFormatters = new Map<string, Intl.DateTimeFormat>();

function queueTimestampFormatter(iana: string): Intl.DateTimeFormat {
  const cached = queueTimestampFormatters.get(iana);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    hourCycle: 'h23',
  });
  queueTimestampFormatters.set(iana, formatter);
  return formatter;
}

function timezoneAbbreviationFormatter(iana: string): Intl.DateTimeFormat {
  const cached = timezoneAbbreviationFormatters.get(iana);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: iana,
    timeZoneName: 'short',
  });
  timezoneAbbreviationFormatters.set(iana, formatter);
  return formatter;
}

/** Events tables: `MM/DD/YYYY HH:mm:ss TZ`. Explicit IANA = site clock; otherwise the browser zone. */
export function formatQueueTimestamp(value: string, timezone?: string | null): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  const iana = resolveDisplayTimeZone(timezone);
  const parts = queueTimestampFormatter(iana).formatToParts(date);

  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';
  const tzLabel = shortTimezoneAbbreviation(date, iana) ?? iana;
  return `${get('month')}/${get('day')}/${get('year')} ${get('hour')}:${get('minute')}:${get('second')} ${tzLabel}`;
}

/** Prefer regional DST abbrs (CDT/EDT); reject offset-only GMT/UTC labels. */
function shortTimezoneAbbreviation(date: Date, iana: string): string | null {
  const raw =
    timezoneAbbreviationFormatter(iana)
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? '';
  const letters = raw.replace(/[^A-Za-z]/g, '');
  if (!letters || letters === 'GMT' || letters === 'UTC') {
    return null;
  }
  return letters;
}

interface ApiEventDetail {
  eventId: string;
  siteId: number;
  location: string;
  address: string;
  currentTime: string;
  status: string;
}

/** Backend GET /api/dealer/events/{eventId} → GetEventByIdResponse */
interface ApiGetEventByIdResponse {
  items: {
    /** Dealer numeric event number shown in the Event Header. */
    id?: number | null;
    eventId: string;
    siteId: number;
    siteName?: string | null;
    firstAlarmDateTime?: string | null;
    currentEventStatusId?: number | null;
    claimedDateTime?: string | null;
    /** 0 = alarm, 1 = manual patrol, 2 = site check. */
    eventTypeId?: number | null;
  } | null;
}

/** Dealer eventTypeId values that survive a reload of the process view. */
function eventTypeFromDealerId(eventTypeId: number | null | undefined): EventType | undefined {
  switch (eventTypeId) {
    case 0:
      return 'process-event';
    case 1:
      return 'manual-patrol';
    case 2:
      return 'site-check';
    default:
      return undefined;
  }
}

/** Header Event ID matches Resolve: positive integer Dealer id, otherwise the route UUID. */
function headerEventId(numericId: number | null | undefined, eventUuid: string): string {
  return typeof numericId === 'number' && Number.isInteger(numericId) && numericId > 0
    ? String(numericId)
    : eventUuid;
}

interface ApiAlarmRow {
  id?: string;
  alarmId?: string;
  cameraId: number;
  cameraKey?: string;
  indicator?: EventAlarmRow['indicator'];
  status?: string | null;
  timestamp?: string;
  createdDateTime?: string;
  camera?: string;
  description?: string | null;
  detectedObjects?: string | readonly string[] | null;
  clipUrl?: string | null;
  clipUrls?: readonly string[];
  aiTags?: EventAlarmRow['aiTags'];
  flagged?: boolean;
}

interface ApiSiteLocation {
  id?: number | string;
  name?: string;
  address?: string;
  armed?: boolean;
  siteId?: number | string;
  siteName?: string;
  siteAddress?: string;
}

export function mapLocations(res: { items: ApiSiteLocation[] | null | undefined }): SiteLocationsResponse {
  return {
    items: (res.items ?? []).map(
      (item): SiteLocation => ({
        id: String(item.id ?? item.siteId ?? ''),
        name: item.name ?? item.siteName ?? '',
        address: item.address ?? item.siteAddress ?? '',
        armed: item.armed ?? false,
      }),
    ),
  };
}

export function mapDealerSiteList(
  res: { items: ApiSiteLocation[] | null | undefined },
  armed: boolean,
): SiteLocationsResponse {
  return {
    items: (res.items ?? []).map(
      (item): SiteLocation => ({
        id: String(item.siteId ?? item.id ?? ''),
        name: item.siteName ?? item.name ?? '',
        address: item.siteAddress ?? item.address ?? '',
        armed,
      }),
    ),
  };
}

export function mapQueue(res: ApiQueueResponse): EventsQueueResponse {
  return {
    totalCount: res.totalCount,
    items: res.items.map(
      (item): QueuedEvent => ({
        id: item.eventId,
        firstAlarmDetected: formatQueueTimestamp(item.firstAlarmDetected),
        location: item.location,
        priority: Math.round(Number(item.priority)) || 0,
        alarmCount: item.alarmCount,
        type: item.type ?? 'process-event',
      }),
    ),
  };
}

export function mapInProcess(res: ApiQueueResponse): EventsInProcessResponse {
  return {
    totalCount: res.totalCount,
    items: res.items.map((item) => ({
      id: item.eventId,
      firstAlarmDetected: formatQueueTimestamp(item.firstAlarmDetected),
      location: item.location,
      priority: Math.round(Number(item.priority)) || 0,
      alarmCount: item.alarmCount,
      type: item.type ?? 'process-event',
      agent: item.actorName ?? '',
    })),
  };
}

export function mapParked(res: ApiQueueResponse): EventsParkedResponse {
  return {
    totalCount: res.totalCount,
    items: res.items.map(
      (item): ParkedEventRow => ({
        id: item.eventId,
        siteId: item.siteId,
        firstAlarmDetected: formatQueueTimestamp(item.firstAlarmDetected),
        location: item.location,
        parkedAt: item.actorAt
          ? formatQueueTimestamp(item.actorAt)
          : formatQueueTimestamp(item.createdDateTime),
        parkedBy: item.actorName ?? '',
        durationMinutes: 15,
        expiresAt: null,
      }),
    ),
  };
}

export function mapEventDetail(dto: ApiEventDetail | ApiGetEventByIdResponse): EventDetail {
  if ('items' in dto) {
    const item = dto.items;
    if (!item) {
      throw new Error('Event not found');
    }
    const type = eventTypeFromDealerId(item.eventTypeId);
    return {
      id: item.eventId,
      location: item.siteName ?? String(item.siteId),
      eventId: headerEventId(item.id, item.eventId),
      address: '',
      currentTime: new Date().toISOString(),
      siteId: item.siteId,
      ...(type ? { type } : {}),
      ...(item.claimedDateTime?.trim()
        ? { claimedAt: resolveProcessingClaimedAt(item.claimedDateTime) }
        : {}),
    };
  }

  return {
    id: dto.eventId,
    location: dto.location,
    eventId: dto.eventId,
    address: dto.address,
    currentTime: dto.currentTime,
    siteId: dto.siteId,
  };
}

export function parseClipLocations(raw: string | null | undefined): string[] {
  if (!raw) {
    return [];
  }

  const seen = new Set<string>();
  const urls: string[] = [];
  // Dealer `clipUrl` is a newline-delimited list of image URLs.
  for (const part of raw.split('\n')) {
    const url = part.trim();
    if (!url || seen.has(url)) {
      continue;
    }
    seen.add(url);
    urls.push(url);
  }
  return urls;
}

const INDICATOR_NAMES: ReadonlySet<string> = new Set(['motion', 'human', 'vehicle', 'gun', 'none']);

/** Returns the variant a detection label maps to, or null when the label is not one we know. */
export function recognizedVariant(label: string): EventAiTag['variant'] | null {
  const normalized = label.trim().toLowerCase();
  if (normalized === 'person' || normalized === 'human' || normalized === 'people') {
    return 'human';
  }
  if (normalized === 'vehicle' || normalized === 'car' || normalized === 'truck') {
    return 'vehicle';
  }
  if (normalized === 'gun' || normalized === 'weapon') {
    return 'gun';
  }
  if (normalized === 'motion') {
    return 'motion';
  }
  return null;
}

export function detectionVariant(label: string, fallback: EventAiTag['variant']): EventAiTag['variant'] {
  return recognizedVariant(label) ?? fallback;
}

function readNumber(record: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
  }
  return 0;
}

function readString(record: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function parseDetectionName(value: unknown): string {
  const record = asRecord(value);
  return record ? readString(record, 'Name', 'name', 'label', 'type') : '';
}

function parseDetectionItem(value: unknown): EventAlarmDetection | null {
  const record = asRecord(value);
  if (!record) {
    return null;
  }
  const name = parseDetectionName(record);
  if (!name) {
    return null;
  }
  const instance = asRecord(record['Instance'] ?? record['instance']);
  if (!instance) {
    return null;
  }
  return {
    bounds: {
      height: readNumber(instance, 'Height', 'height'),
      left: readNumber(instance, 'Left', 'left'),
      top: readNumber(instance, 'Top', 'top'),
      width: readNumber(instance, 'Width', 'width'),
    },
    confidence: readNumber(record, 'Confidence', 'confidence'),
    name,
    sourceWidth: readNumber(record, 'Width', 'width'),
    sourceHeight: readNumber(record, 'Height', 'height'),
  };
}

/** Nested frame-group payload: every top-level element is itself an array (possibly empty). */
function isNestedDetectionGroups(parsed: unknown): parsed is unknown[][] {
  return Array.isArray(parsed) && parsed.every((item) => Array.isArray(item));
}

function ingestDetectionLabelsOnly(value: unknown, labels: string[]): void {
  const detection = parseDetectionItem(value);
  if (detection) {
    labels.push(detection.name);
    return;
  }
  const name = parseDetectionName(value);
  if (name) {
    labels.push(name);
  }
}

function ingestParsedDetectionItems(
  parsed: unknown,
  detections: EventAlarmDetection[],
  labels: string[],
): void {
  if (!Array.isArray(parsed)) {
    // Lone objects are labels-only — drawable boxes require nested frame groups.
    ingestDetectionLabelsOnly(parsed, labels);
    return;
  }

  if (isNestedDetectionGroups(parsed)) {
    parsed.forEach((group, groupIndex) => {
      for (const item of group) {
        if (typeof item === 'string') {
          const inner = parseDetectedObjects(item);
          labels.push(...inner.labels);
          for (const detection of inner.detections) {
            detections.push({
              ...detection,
              frameGroupIndex: detection.frameGroupIndex ?? groupIndex,
            });
          }
          continue;
        }
        const detection = parseDetectionItem(item);
        if (detection) {
          detections.push({ ...detection, frameGroupIndex: groupIndex });
          labels.push(detection.name);
          continue;
        }
        ingestDetectionLabelsOnly(item, labels);
      }
    });
    return;
  }

  // Flat object arrays / mixed legacy shapes keep tags but never draw boxes.
  for (const item of parsed) {
    if (typeof item === 'string') {
      const inner = parseDetectedObjects(item);
      detections.push(...inner.detections);
      labels.push(...inner.labels);
      continue;
    }
    ingestDetectionLabelsOnly(item, labels);
  }
}

/** Dev API payloads sometimes omit the outer `[`/`]` and typo the Height key. */
function repairMalformedDetectionJson(raw: string): string {
  let repaired = raw.trim();
  if (repaired.startsWith('{') && !repaired.startsWith('[')) {
    repaired = `[${repaired}]`;
  }
  // `"Width": 0,Height": 0` → `"Width": 0,"Height": 0`
  repaired = repaired.replace(/,\s*Height"/g, ',"Height"');
  return repaired;
}

/** Last-resort label extraction when JSON parsing fails entirely. */
function extractDetectionNamesFromText(raw: string): string[] {
  const names: string[] = [];
  const patterns = [
    /"Name"\s*:\s*"([^"]+)"/gi,
    /Name\\?"?\s*:\s*\\?"([a-zA-Z][a-zA-Z0-9_-]*)/gi,
  ];
  for (const pattern of patterns) {
    for (const match of raw.matchAll(pattern)) {
      const name = match[1]?.trim();
      if (name) {
        names.push(name);
      }
    }
    if (names.length > 0) {
      break;
    }
  }
  return names;
}

/** A label is a short word like "person" — never JSON, a number, or a coordinate blob. */
function isPlausibleLabel(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length > 0 &&
    trimmed.length <= 40 &&
    !/[[\]{}":]/.test(trimmed) &&
    !/^[-+]?\d*\.?\d+$/.test(trimmed)
  );
}

/**
 * Alarm detections arrive double-encoded: `DetectedObjects` is an array of strings, each of
 * which is itself JSON. Nested `[[{…}],[{…}],[{…}]]` frame groups produce drawable detections
 * (`frameGroupIndex` 0/1/2). Flat object arrays and label strings keep tags only — no boxes.
 */
export function parseDetectedObjects(
  raw: string | readonly string[] | null | undefined,
): {
  labels: string[];
  detections: EventAlarmDetection[];
} {
  const sources = (Array.isArray(raw) ? raw : [raw]).filter(
    (item): item is string => typeof item === 'string' && item.trim().length > 0,
  );
  if (sources.length === 0) {
    return { labels: [], detections: [] };
  }

  const detections: EventAlarmDetection[] = [];
  const labels: string[] = [];

  for (const source of sources) {
    const trimmed = source.trim();
    let handled = false;

    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed) || asRecord(parsed)) {
        ingestParsedDetectionItems(parsed, detections, labels);
        handled = true;
      }
    } catch {
      // Not strict JSON — try repaired dev payloads, then regex name extraction.
    }

    if (!handled) {
      try {
        const repaired = repairMalformedDetectionJson(trimmed);
        const parsed: unknown = JSON.parse(repaired);
        ingestParsedDetectionItems(parsed, detections, labels);
        handled = true;
      } catch {
        // Still not JSON.
      }
    }

    if (!handled) {
      const extracted = extractDetectionNamesFromText(trimmed);
      if (extracted.length > 0) {
        labels.push(...extracted);
        handled = true;
      }
    }

    if (handled) {
      continue;
    }

    labels.push(...trimmed.split(',').map((part) => part.trim()).filter(isPlausibleLabel));
  }

  const seen = new Set<string>();
  return {
    detections,
    labels: labels.filter((label) => {
      const key = label.toLowerCase();
      if (!isPlausibleLabel(label) || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    }),
  };
}

export function mapAlarms(res: { items: ApiAlarmRow[] | null | undefined }): EventAlarmsResponse {
  return {
    items: (res.items ?? []).map((row) => {
      const status = (row.status ?? '').toLowerCase();
      const flagged = row.flagged ?? status.includes('flag');
      const parsed = parseDetectedObjects(row.detectedObjects);
      const description = row.description?.trim() ?? '';
      const descriptionIsIndicator = INDICATOR_NAMES.has(description.toLowerCase());
      const firstLabelVariant =
        parsed.labels.length > 0
          ? detectionVariant(parsed.labels[0]!, 'motion')
          : 'human';
      const indicator =
        row.indicator ??
        (descriptionIsIndicator
          ? (description.toLowerCase() as EventAlarmRow['indicator'])
          : status.includes('vehicle')
            ? 'vehicle'
            : status.includes('motion')
              ? 'motion'
              : status.includes('gun')
                ? 'gun'
                : firstLabelVariant);
      const id = row.id ?? row.alarmId ?? '';
      const fallbackVariant = (
        indicator === 'vehicle' || indicator === 'motion' || indicator === 'human' || indicator === 'gun'
          ? indicator
          : 'motion'
      ) as EventAiTag['variant'];
      const tags: EventAiTag[] =
        row.aiTags ??
        parsed.labels.map((label) => ({
          label,
          variant: detectionVariant(label, fallbackVariant),
        }));
      const cameraNumeric = Number(row.cameraId);
      const cameraId =
        row.cameraKey?.trim()?.replace(/^cam-/, '') ||
        (Number.isFinite(cameraNumeric) ? String(cameraNumeric) : String(row.cameraId ?? ''));
      return {
        id,
        cameraId,
        indicator: indicator as EventAlarmRow['indicator'],
        timestamp: row.timestamp ?? row.createdDateTime ?? new Date().toISOString(),
        camera:
          row.camera ??
          (descriptionIsIndicator || !description ? `Camera ${row.cameraId}` : description),
        aiTags: tags,
        flagged,
        clipUrls: row.clipUrls?.length ? [...row.clipUrls] : parseClipLocations(row.clipUrl),
        detections: parsed.detections,
      };
    }),
  };
}

export function mapParkResponse(
  eventId: string,
  body: { durationMinutes: number; location?: string; patrolStarted?: string; parkedBy?: string },
): ParkEventResponse {
  return {
    success: true,
    parked: {
      id: eventId,
      siteId: null,
      firstAlarmDetected: body.patrolStarted ?? '',
      location: body.location ?? '',
      parkedAt: new Date().toISOString(),
      parkedBy: body.parkedBy ?? '',
      durationMinutes: body.durationMinutes,
      expiresAt: null,
    },
  };
}

export function tapMap<T, R>(project: (value: T) => R) {
  return (source: Observable<T>): Observable<R> => source.pipe(map(project));
}
